// src/components/MyCoastImage.jsx
// MyCoast图片组件 - 清理版本，删除所有fallback和模拟数据

import React, { useState, useEffect } from 'react';
import { Camera } from 'lucide-react';

/**
 * MyCoast图片组件 - 只使用真实MyCoast数据，失败时显示"无图片"
 */
const MyCoastImage = ({ 
  src, 
  alt = "MyCoast flood image", 
  className = "",
  showPlaceholder = true,
  onClick = null,
  style = {}
}) => {
  const [imageState, setImageState] = useState('loading'); // loading, loaded, error, proxy
  const [currentSrc, setCurrentSrc] = useState(src);

  // CORS代理URL - 保持原有的代理尝试逻辑
  const CORS_PROXIES = [
    'https://api.codetabs.com/v1/proxy?quest=',
    'https://api.allorigins.win/get?url=',
    'https://cors-anywhere.herokuapp.com/'
  ];

  // 重置状态当src改变时
  useEffect(() => {
    if (src) {
      setImageState('loading');
      setCurrentSrc(src);
    } else {
      setImageState('error');
    }
  }, [src]);

  // 处理图片加载成功
  const handleLoad = () => {
    setImageState('loaded');
  };

  // 处理图片加载失败
  const handleError = () => {
    if (currentSrc === src) {
      // 第一次失败，尝试通过代理访问
      console.log('🖼️ Direct MyCoast image access failed, trying proxy...');
      setImageState('proxy');
      setCurrentSrc(`${CORS_PROXIES[0]}${encodeURIComponent(src)}`);
    } else if (currentSrc.includes(CORS_PROXIES[0])) {
      // 第一个代理失败，尝试第二个
      console.log('🖼️ First proxy failed, trying second proxy...');
      setCurrentSrc(`${CORS_PROXIES[1]}${src}`);
    } else if (currentSrc.includes(CORS_PROXIES[1])) {
      // 第二个代理失败，尝试第三个
      console.log('🖼️ Second proxy failed, trying third proxy...');
      setCurrentSrc(`${CORS_PROXIES[2]}${src}`);
    } else {
      // 所有代理都失败了
      console.log('🖼️ All proxies failed for MyCoast image');
      setImageState('error');
    }
  };

  // 点击处理
  const handleClick = (e) => {
    if (onClick) {
      onClick(e);
    }
  };

  // 根据状态渲染不同内容
  if (imageState === 'error') {
    if (showPlaceholder) {
      return (
        <div 
          className={`flex flex-col items-center justify-center bg-gray-50 border border-gray-200 rounded ${className}`}
          style={{ minHeight: '120px', ...style }}
          onClick={handleClick}
        >
          <Camera className="h-8 w-8 text-gray-400 mb-2" />
          <span className="text-xs text-gray-500 text-center px-2">
            No MyCoast Image
            <br />
            <span className="text-gray-400">(Failed to load)</span>
          </span>
        </div>
      );
    }
    return null; // 不显示任何内容
  }

  return (
    <div className="relative">
      <img
        src={currentSrc}
        alt={alt}
        className={className}
        style={style}
        onLoad={handleLoad}
        onError={handleError}
        onClick={handleClick}
      />
      
      {/* 加载状态指示器 */}
      {imageState === 'loading' && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 bg-opacity-75 rounded">
          <div className="text-center">
            <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin mx-auto mb-2"></div>
            <span className="text-xs text-gray-600">Loading MyCoast...</span>
          </div>
        </div>
      )}
      
      {/* 代理状态指示器 */}
      {imageState === 'proxy' && (
        <div className="absolute top-1 right-1 bg-orange-500 text-white text-xs px-1 py-0.5 rounded">
          Proxy
        </div>
      )}
    </div>
  );
};

export default MyCoastImage;